/**
 * Sanitizes user input to prevent XSS and injection attacks
 * SECURITY: Removes dangerous content before storing in database
 *
 * @param input The user input to sanitize
 * @param maxLength Maximum allowed length (default: 1000)
 * @returns Sanitized string
 */
export const sanitizeUserInput = (input: string, maxLength: number = 1000): string => {
  // Remove null bytes
  let sanitized = input.replace(/\0/g, '');

  // Strip HTML tags
  sanitized = sanitized.replace(/<[^>]*>/g, '');

  // Remove javascript: and data: protocols
  sanitized = sanitized.replace(/javascript:/gi, '');
  sanitized = sanitized.replace(/data:/gi, '');

  // Limit length to prevent DoS
  sanitized = sanitized.substring(0, maxLength);

  // Trim whitespace
  return sanitized.trim();
};

/**
 * Sanitizes search query input to prevent injection attacks
 * SECURITY: Removes SQL-like operators and special characters from search queries
 *
 * @param query The search query to sanitize
 * @param maxLength Maximum allowed length (default: 100)
 * @returns Sanitized search query
 */
export const sanitizeSearchQuery = (query: string, maxLength: number = 100): string => {
  // Remove null bytes
  let sanitized = query.replace(/\0/g, '');

  // Remove SQL-like operators and special characters
  sanitized = sanitized.replace(/[';"<>]/g, '');

  // Remove multiple spaces
  sanitized = sanitized.replace(/\s+/g, ' ');

  // Remove leading/trailing wildcards to prevent performance issues
  sanitized = sanitized.replace(/^[*%]+|[*%]+$/g, '');

  // Limit length to prevent DoS
  sanitized = sanitized.substring(0, maxLength);

  // Trim whitespace
  return sanitized.trim();
};

/**
 * Sanitizes error messages for production to prevent information leakage
 * SECURITY: Hides internal details like file paths, stack traces, and database info
 *
 * @param error The error object or message
 * @param userFriendlyMessage Optional custom user-friendly message
 * @returns Sanitized error message safe for displaying to users
 */
export const sanitizeErrorMessage = (
  error: Error | string | unknown,
  userFriendlyMessage?: string
): string => {
  // In development, return full error details
  if (process.env.NODE_ENV === 'development') {
    if (error instanceof Error) {
      return error.message;
    }
    return String(error);
  }

  // In production, return generic message or user-friendly message
  if (userFriendlyMessage) {
    return userFriendlyMessage;
  }

  // Map common error patterns to user-friendly messages
  const errorString = error instanceof Error ? error.message : String(error);

  if (/network|fetch|connection/i.test(errorString)) {
    return 'Network error. Please check your connection and try again.';
  }

  if (/permission|denied|unauthorized/i.test(errorString)) {
    return 'You do not have permission to perform this action.';
  }

  if (/not found|404/i.test(errorString)) {
    return 'The requested resource was not found.';
  }

  if (/timeout/i.test(errorString)) {
    return 'The request timed out. Please try again.';
  }

  if (/firebase|database|firestore/i.test(errorString)) {
    return 'A database error occurred. Please try again later.';
  }

  // Default generic message that doesn't leak implementation details
  return 'An error occurred. Please try again later.';
};

/**
 * Validates a bio field
 * SECURITY: Ensures bio doesn't contain script tags or exceed length limits
 *
 * @param bio The bio text to validate
 * @returns true if valid, false otherwise
 */
export const isValidBio = (bio: string): boolean => {
  // Max length 500 characters
  if (bio.length > 500) return false;

  // No script tags
  if (/<script/i.test(bio)) return false;

  // No javascript: protocol
  if (/javascript:/i.test(bio)) return false;

  // No data: protocol (prevents data URIs)
  if (/data:/i.test(bio)) return false;

  return true;
};

/**
 * Validates an email address
 *
 * @param email The email address to validate
 * @returns true if valid, false otherwise
 */
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Validates a password (at least 8 characters, with one letter and one number)
 * 
 * @param password The password to validate
 * @returns true if valid, false otherwise
 */
export const isValidPassword = (password: string): boolean => {
  // At least 8 characters, at least one letter and one number
  const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d]{8,}$/;
  return passwordRegex.test(password);
};

/**
 * Validates a username (3-30 characters, letters, numbers, and underscore only)
 * 
 * @param username The username to validate
 * @returns true if valid, false otherwise
 */
export const isValidUsername = (username: string): boolean => {
  // 3-30 characters, letters, numbers, and underscore only
  const usernameRegex = /^[a-zA-Z0-9_]{3,30}$/;
  return usernameRegex.test(username);
};

/**
 * Validates that a string is not empty
 * 
 * @param value The string to validate
 * @returns true if not empty, false otherwise
 */
export const isNotEmpty = (value: string): boolean => {
  return value.trim().length > 0;
};

/**
 * Validates a name (letters, spaces, hyphens, and apostrophes only)
 * 
 * @param name The name to validate
 * @returns true if valid, false otherwise
 */
export const isValidName = (name: string): boolean => {
  // Letters, spaces, hyphens, and apostrophes only
  const nameRegex = /^[a-zA-Z\s'-]+$/;
  return nameRegex.test(name);
};

/**
 * Validates a URL
 * 
 * @param url The URL to validate
 * @returns true if valid, false otherwise
 */
export const isValidUrl = (url: string): boolean => {
  try {
    new URL(url);
    return true;
  } catch (error) {
    return false;
  }
};

/**
 * Validates a YouTube video ID
 * 
 * @param videoId The YouTube video ID to validate
 * @returns true if valid, false otherwise
 */
export const isValidYouTubeId = (videoId: string): boolean => {
  // YouTube video IDs are 11 characters
  const youtubeRegex = /^[a-zA-Z0-9_-]{11}$/;
  return youtubeRegex.test(videoId);
};

/**
 * Extracts a YouTube video ID from a YouTube URL
 * 
 * @param url The YouTube URL
 * @returns The video ID or null if not found
 */
export const extractYouTubeId = (url: string): string | null => {
  try {
    // Try to parse the URL
    const parsedUrl = new URL(url);
    
    // youtube.com/watch?v=VIDEO_ID
    if (parsedUrl.hostname.includes('youtube.com') && parsedUrl.pathname === '/watch') {
      const videoId = parsedUrl.searchParams.get('v');
      if (videoId && isValidYouTubeId(videoId)) {
        return videoId;
      }
    }
    
    // youtu.be/VIDEO_ID
    if (parsedUrl.hostname === 'youtu.be' && parsedUrl.pathname.length > 1) {
      const videoId = parsedUrl.pathname.substring(1);
      if (isValidYouTubeId(videoId)) {
        return videoId;
      }
    }
    
    // youtube.com/embed/VIDEO_ID
    if (parsedUrl.hostname.includes('youtube.com') && parsedUrl.pathname.startsWith('/embed/')) {
      const videoId = parsedUrl.pathname.substring(7);
      if (isValidYouTubeId(videoId)) {
        return videoId;
      }
    }
    
    return null;
  } catch (error) {
    return null;
  }
};

/**
 * Validates that a number is within a specified range
 * 
 * @param value The number to validate
 * @param min The minimum value (inclusive)
 * @param max The maximum value (inclusive)
 * @returns true if within range, false otherwise
 */
export const isInRange = (value: number, min: number, max: number): boolean => {
  return value >= min && value <= max;
};

/**
 * Validates that a date is not in the future
 * 
 * @param date The date to validate (timestamp in milliseconds)
 * @returns true if not in the future, false otherwise
 */
export const isNotFutureDate = (date: number): boolean => {
  return date <= Date.now();
};

/**
 * Gets validation error messages for a form
 * 
 * @param form The form data to validate
 * @param rules The validation rules
 * @returns An object containing error messages for each field
 */
export const getValidationErrors = <T extends Record<string, any>>(
  form: T,
  rules: {
    [key in keyof T]?: Array<{
      test: (value: any) => boolean;
      message: string;
    }>;
  }
): Partial<Record<keyof T, string>> => {
  const errors: Partial<Record<keyof T, string>> = {};
  
  // Check each field
  for (const field in rules) {
    const fieldRules = rules[field];
    const value = form[field];
    
    // Skip if no rules for this field
    if (!fieldRules) continue;
    
    // Check each rule for this field
    for (const rule of fieldRules) {
      if (!rule.test(value)) {
        errors[field] = rule.message;
        break;
      }
    }
  }
  
  return errors;
};

/**
 * Validation result for training session data
 */
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
}

/**
 * Validates a training session
 * 
 * @param session The training session to validate
 * @returns ValidationResult with isValid flag and array of error messages
 */
export const validateTrainingSession = (session: {
  totalTrainedTime: number;
  rollTime?: number;
  date: Date | string | number;
  type?: string;
  location?: { name: string };
}): ValidationResult => {
  const errors: string[] = [];
  
  // Convert date to Date object for validation
  let sessionDate: Date;
  if (typeof session.date === 'string') {
    sessionDate = new Date(session.date);
  } else if (typeof session.date === 'number') {
    sessionDate = new Date(session.date);
  } else {
    sessionDate = session.date;
  }
  
  // Validate that date is not invalid
  if (isNaN(sessionDate.getTime())) {
    errors.push('Training date is invalid');
  } else {
    // Date cannot be in future (allow same day)
    const today = new Date();
    today.setHours(23, 59, 59, 999); // End of today
    if (sessionDate > today) {
      errors.push('Training date cannot be in the future');
    }
    
    // Warn about very old dates (more than 10 years ago)
    const tenYearsAgo = new Date();
    tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
    if (sessionDate < tenYearsAgo) {
      errors.push('Warning: Training date is more than 10 years ago. Please verify this is correct.');
    }
    
    // Prevent dates before 1990 (likely data entry error)
    const minDate = new Date('1990-01-01');
    if (sessionDate < minDate) {
      errors.push('Training date cannot be before 1990');
    }
  }
  
  // Duration must be positive
  if (session.totalTrainedTime <= 0) {
    errors.push('Training time must be greater than 0 minutes');
  }
  
  // Duration should be reasonable (max 24 hours = 1440 minutes)
  if (session.totalTrainedTime > 1440) {
    errors.push('Training time cannot exceed 24 hours');
  }
  
  // Roll time validation
  if (session.rollTime !== undefined && session.rollTime !== null) {
    // Roll time cannot be negative
    if (session.rollTime < 0) {
      errors.push('Roll time cannot be negative');
    }
    
    // Roll time cannot exceed total training time
    if (session.rollTime > session.totalTrainedTime) {
      errors.push('Roll time cannot exceed total training time');
    }
  }
  
  // Type validation (if provided)
  if (session.type) {
    // Match original app types exactly
    const validTypes = ['training', 'seminar', 'camp', 'teaching'];
    if (!validTypes.includes(session.type)) {
      errors.push('Invalid training type');
    }
  }
  
  // Location validation (if provided)
  if (session.location && (!session.location.name || session.location.name.trim().length === 0)) {
    errors.push('Location name cannot be empty');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates training time inputs
 * 
 * @param totalTime Total training time in minutes
 * @param rollTime Roll time in minutes (optional)
 * @returns ValidationResult
 */
export const validateTrainingTimes = (totalTime: number, rollTime?: number): ValidationResult => {
  const errors: string[] = [];
  
  if (totalTime <= 0) {
    errors.push('Total training time must be greater than 0');
  }
  
  if (totalTime > 1440) {
    errors.push('Total training time cannot exceed 24 hours');
  }
  
  if (rollTime !== undefined && rollTime !== null) {
    if (rollTime < 0) {
      errors.push('Roll time cannot be negative');
    }
    
    if (rollTime > totalTime) {
      errors.push('Roll time cannot exceed total training time');
    }
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates a date for training sessions
 * 
 * @param date The date to validate
 * @returns ValidationResult
 */
export const validateTrainingDate = (date: Date | string | number): ValidationResult => {
  const errors: string[] = [];
  
  let sessionDate: Date;
  if (typeof date === 'string') {
    sessionDate = new Date(date);
  } else if (typeof date === 'number') {
    sessionDate = new Date(date);
  } else {
    sessionDate = date;
  }
  
  if (isNaN(sessionDate.getTime())) {
    errors.push('Invalid date');
    return { isValid: false, errors };
  }
  
  // Date cannot be more than 1 day in the future (to account for timezone differences)
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(23, 59, 59, 999);
  
  if (sessionDate > tomorrow) {
    errors.push('Training date cannot be in the future');
  }
  
  // Date cannot be more than 10 years in the past (reasonable limit)
  const tenYearsAgo = new Date();
  tenYearsAgo.setFullYear(tenYearsAgo.getFullYear() - 10);
  
  if (sessionDate < tenYearsAgo) {
    errors.push('Training date cannot be more than 10 years ago');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates belt promotion data based on Phoenix app structure
 * 
 * @param promotion The belt promotion to validate
 * @param existingPromotions Array of existing promotions for comparison
 * @returns ValidationResult
 */
export const validateBeltPromotion = (promotion: {
  date: number;
  beltColor: string;
  stripes: number;
  martialArt: string;
}, existingPromotions: Array<{
  date: number;
  beltColor: string;
  stripes: number;
  martialArt: string;
}>): ValidationResult => {
  const errors: string[] = [];
  
  // Date validation
  if (isNaN(promotion.date)) {
    errors.push('Promotion date is invalid');
  } else {
    // Date cannot be more than 1 day in the future (timezone tolerance)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);
    
    if (promotion.date > tomorrow.getTime()) {
      errors.push('Promotion date cannot be in the future');
    }
    
    // Date cannot be more than 100 years ago (reasonable historical limit)
    const centuryAgo = new Date();
    centuryAgo.setFullYear(centuryAgo.getFullYear() - 100);
    
    if (promotion.date < centuryAgo.getTime()) {
      errors.push('Promotion date cannot be more than 100 years ago');
    }
  }
  
  // Belt color validation
  if (!promotion.beltColor || promotion.beltColor.trim().length === 0) {
    errors.push('Belt color is required');
  }
  // Note: Don't validate specific belt colors - original app allows custom belts
  // and different martial arts have different belt color systems
  
  // Stripe count validation (from original app: max 10 stripes)
  if (promotion.stripes !== undefined) {
    if (promotion.stripes < 0) {
      errors.push('Stripe count cannot be negative');
    }
    if (promotion.stripes > 10) {
      errors.push('Stripe count cannot exceed 10');
    }
  }
  
  // Martial art validation
  if (!promotion.martialArt || promotion.martialArt.trim().length === 0) {
    errors.push('Martial art name is required');
  }
  
  // Check for duplicate promotions on the same date for the same martial art
  // This prevents data inconsistencies while following original app's permissive approach
  const sameArtPromotions = existingPromotions.filter(p => 
    p.martialArt === promotion.martialArt
  );
  
  const sameDate = sameArtPromotions.find(p => {
    const pDate = new Date(p.date);
    const newDate = new Date(promotion.date);
    return pDate.getFullYear() === newDate.getFullYear() &&
           pDate.getMonth() === newDate.getMonth() &&
           pDate.getDate() === newDate.getDate();
  });
  
  if (sameDate) {
    errors.push('A promotion already exists for this martial art on this date');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates belt progression sequence (optional - original app doesn't enforce this)
 * This is for informational purposes only, not for blocking saves
 * 
 * @param currentBelt Current belt color
 * @param newBelt New belt color being promoted to
 * @param level Belt level (adult/junior)
 * @returns ValidationResult with warnings for unusual progressions
 */
export const validateBeltProgression = (
  currentBelt: string,
  newBelt: string,
  level: 'adult' | 'junior'
): ValidationResult => {
  const warnings: string[] = [];
  
  // Standard adult progression for BJJ
  const adultProgression = ['white', 'blue', 'purple', 'brown', 'black'];
  
  // Junior progression (more complex, varies by age)
  const juniorProgression = ['white', 'yellow', 'orange', 'green', 'blue', 'purple', 'brown', 'black'];
  
  const progression = level === 'adult' ? adultProgression : juniorProgression;
  
  const currentIndex = progression.indexOf(currentBelt.toLowerCase());
  const newIndex = progression.indexOf(newBelt.toLowerCase());
  
  if (currentIndex === -1 || newIndex === -1) {
    // Unknown belt colors, no validation
    return { isValid: true, errors: [] };
  }
  
  // Check if progression is backwards
  if (newIndex < currentIndex) {
    warnings.push('This appears to be a demotion - promotions typically progress forward');
  }
  
  // Check if progression skips belts
  if (newIndex > currentIndex + 1) {
    warnings.push('This promotion skips intermediate belt levels');
  }
  
  // Check if promotion is to the same belt (only makes sense with different stripe counts)
  if (newIndex === currentIndex) {
    warnings.push('Promotion is to the same belt color - ensure stripe count is different');
  }
  
  return {
    isValid: true, // Always valid, just warnings
    errors: warnings
  };
};

/**
 * Validates competition data based on original Marune app structure
 * 
 * @param competition The competition data to validate
 * @returns ValidationResult
 */
export const validateCompetition = (competition: {
  tournamentName: string;
  date: number;
  winsCount?: number;
  lossesCount?: number;
  rank?: 0 | 1 | 2 | 3;
  rounds?: Array<{ status: 'won' | 'lost' | 'tie'; opponentName?: string }>;
  location?: { name: string };
}): ValidationResult => {
  const errors: string[] = [];
  
  // Tournament name validation
  if (!competition.tournamentName || competition.tournamentName.trim().length === 0) {
    errors.push('Tournament name is required');
  }
  
  // Date validation
  if (isNaN(competition.date)) {
    errors.push('Competition date is invalid');
  } else {
    // Date cannot be more than 1 day in the future (timezone tolerance)
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    tomorrow.setHours(23, 59, 59, 999);
    
    if (competition.date > tomorrow.getTime()) {
      errors.push('Competition date cannot be in the future');
    }
    
    // Date cannot be more than 50 years ago (reasonable limit for competition history)
    const fiftyYearsAgo = new Date();
    fiftyYearsAgo.setFullYear(fiftyYearsAgo.getFullYear() - 50);
    
    if (competition.date < fiftyYearsAgo.getTime()) {
      errors.push('Competition date cannot be more than 50 years ago');
    }
  }
  
  // Wins/losses validation
  if (competition.winsCount !== undefined && competition.winsCount < 0) {
    errors.push('Wins count cannot be negative');
  }
  
  if (competition.lossesCount !== undefined && competition.lossesCount < 0) {
    errors.push('Losses count cannot be negative');
  }
  
  // Rank validation (0=none, 1=gold, 2=silver, 3=bronze)
  if (competition.rank !== undefined) {
    if (![0, 1, 2, 3].includes(competition.rank)) {
      errors.push('Invalid rank value. Must be 0 (no medal), 1 (gold), 2 (silver), or 3 (bronze)');
    }
  }
  
  // Rounds array validation - max 30 rounds as per original app
  if (competition.rounds && competition.rounds.length > 30) {
    errors.push('Cannot have more than 30 rounds in a competition');
  }
  
  // Location validation (if provided) - allow empty location objects
  if (competition.location && competition.location.name && competition.location.name.trim().length === 0) {
    errors.push('Location name cannot be empty if location is provided');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Validates competition match/round data
 * 
 * @param round The round/match data to validate
 * @returns ValidationResult
 */
export const validateCompetitionRound = (round: {
  status: 'won' | 'lost' | 'tie';
  opponentName?: string;
}): ValidationResult => {
  const errors: string[] = [];
  
  // Status validation
  if (!round.status || !['won', 'lost', 'tie'].includes(round.status)) {
    errors.push('Round status must be "won", "lost", or "tie"');
  }
  
  // Opponent name is optional but if provided should not be empty
  if (round.opponentName !== undefined && round.opponentName.trim().length === 0) {
    errors.push('Opponent name cannot be empty if provided');
  }
  
  return {
    isValid: errors.length === 0,
    errors
  };
};

/**
 * Calculates wins and losses from rounds array
 * This follows the original app's approach of counting rounds
 * 
 * @param rounds Array of competition rounds
 * @returns Object with winsCount and lossesCount
 */
export const calculateCompetitionScore = (rounds: Array<{ status: 'won' | 'lost' | 'tie' }>): {
  winsCount: number;
  lossesCount: number;
  tieCount: number;
} => {
  let winsCount = 0;
  let lossesCount = 0;
  let tieCount = 0;
  
  rounds.forEach(round => {
    if (round.status === 'won') {
      winsCount++;
    } else if (round.status === 'lost') {
      lossesCount++;
    } else if (round.status === 'tie') {
      tieCount++;
    }
  });
  
  return { winsCount, lossesCount, tieCount };
};

/**
 * Determines medal assignment based on tournament placement
 * Following original app's rank system
 * 
 * @param placement Tournament placement (1st, 2nd, 3rd, etc.)
 * @returns Rank value (0=none, 1=gold, 2=silver, 3=bronze)
 */
export const getMedalFromPlacement = (placement: number): 0 | 1 | 2 | 3 => {
  switch (placement) {
    case 1:
      return 1; // Gold
    case 2:
      return 2; // Silver
    case 3:
      return 3; // Bronze
    default:
      return 0; // No medal
  }
};